import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PlayerCard } from "@/components/PlayerCard";
import { PlayerSilhouette } from "@/components/PlayerSilhouette";
import { IMAGES } from "@/lib/images";
import { useSeo } from "@/lib/useSeo";
import { listPlayers, listStaff } from "@/lib/db";

const POSITION_GROUPS = {
  football: [
    { key: "gk", label: "Τερματοφύλακες" },
    { key: "def", label: "Αμυντικοί" },
    { key: "mid", label: "Μέσοι" },
    { key: "fwd", label: "Επιθετικοί" },
  ],
  basketball: [
    { key: "guard", label: "Γκαρντ" },
    { key: "forward", label: "Φόργουορντ" },
    { key: "center", label: "Σέντερ" },
  ],
};

const FootballIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7l4 3-1.5 4.5h-5L8 10z" fill="currentColor" stroke="none" />
    <path d="M12 3v4M4.5 9l3.5 1M19.5 9L16 10M7 19l1.5-4M17 19l-1.5-4" />
  </svg>
);

const BasketballIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3v18M5.6 5.6c3 3 3 9.8 0 12.8M18.4 5.6c-3 3-3 9.8 0 12.8" />
  </svg>
);

export default function Roster() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "basketball" ? "basketball" : "football";
  useSeo({ title: "Ρόστερ", description: "Το ρόστερ της Ρεάλ Δυτικής Αττικής σε ποδόσφαιρο και μπάσκετ — παίκτες ανά θέση και τεχνικό επιτελείο." });
  const [players, setPlayers] = useState([]);
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    listPlayers(tab).then((d) => setPlayers(d)).catch(() => {});
    listStaff(tab).then((d) => setStaff(d)).catch(() => {});
  }, [tab]);

  const grouped = useMemo(() => {
    const map = {};
    for (const g of POSITION_GROUPS[tab]) {
      map[g.key] = players.filter((p) => p.position === g.key);
    }
    return map;
  }, [players, tab]);

  const setTab = (t) => setSearchParams({ tab: t });

  return (
    <div>
      {/* BANNER */}
      <section className="relative flex h-[46vh] min-h-[340px] items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.rosterBanner})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
        <div className="club-container relative z-10 pb-12">
          <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-gold">Οι αθλητές μας</p>
          <h1 className="font-heading mt-2 text-5xl font-900 uppercase tracking-tight text-white sm:text-7xl">
            Το Ρόστερ μας
          </h1>
        </div>
      </section>

      {/* TABS */}
      <div className="sticky top-[68px] z-30 border-b border-white/10 bg-navy/90 backdrop-blur-lg">
        <div className="club-container flex gap-3 py-4">
          {[
            { key: "football", label: "Ποδόσφαιρο", Icon: FootballIcon },
            { key: "basketball", label: "Μπάσκετ", Icon: BasketballIcon },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`roster-tab-${t.key}`}
              className={`relative flex items-center gap-2 rounded-full px-6 py-3 font-display text-lg font-700 uppercase tracking-wide transition-all duration-200 ${
                tab === t.key
                  ? "bg-brand text-white shadow-[0_0_24px_rgba(60,115,192,0.55)]"
                  : "border border-white/15 text-ice hover:border-gold hover:text-gold"
              }`}
            >
              <t.Icon width={22} height={22} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* PLAYERS */}
      <section className="club-container py-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
          >
            {POSITION_GROUPS[tab].map((group) => (
              <div key={group.key} className="mb-16">
                <div className="mb-7 flex items-center gap-4">
                  <h2 className="font-heading text-3xl font-800 uppercase tracking-tight text-white sm:text-4xl">
                    {group.label}
                  </h2>
                  <div className="h-1 flex-1 rounded bg-gradient-to-r from-gold/70 to-transparent" />
                </div>
                {grouped[group.key]?.length ? (
                  <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                    {grouped[group.key].map((p, i) => (
                      <PlayerCard key={p.id} player={p} index={i} />
                    ))}
                  </div>
                ) : (
                  <p className="text-ice/60">Δεν υπάρχουν καταχωρημένοι αθλητές.</p>
                )}
              </div>
            ))}

            {/* COACHING STAFF */}
            <div className="mt-4 rounded-2xl border border-white/10 bg-royal/30 p-8">
              <div className="mb-7 flex items-center gap-3">
                <Users className="text-gold" size={26} />
                <h2 className="font-heading text-3xl font-800 tracking-tight text-white">
                  Τεχνικό Επιτελείο
                </h2>
              </div>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {staff.map((s, i) => (
                  <Reveal key={s.id} delay={i * 0.06}>
                    <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-navy/60 p-4 transition-colors hover:border-brand/50">
                      <div className="h-16 w-16 overflow-hidden rounded-full bg-navy">
                        {s.photo ? (
                          <img src={s.photo} alt={s.name} className="h-full w-full object-cover" />
                        ) : (
                          <PlayerSilhouette className="h-full w-full" />
                        )}
                      </div>
                      <div>
                        <p className="font-display text-xs font-600 uppercase tracking-widest text-gold">
                          {s.role}
                        </p>
                        <p className="font-heading text-xl font-800 leading-tight text-white">
                          {s.name}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>
    </div>
  );
}
